import React, { useRef, useState } from 'react';
import { Button, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Mismo límite real que MAX_FILE_SIZE en backend/routers/files.py — se
// valida acá ANTES de intentar subir para poder mostrar siempre un mensaje
// claro (límite + tamaño real) sin depender de que el backend/proxy
// devuelva un error legible: un archivo de este tamaño puede hacer que el
// proxy de Render corte la conexión antes de que FastAPI llegue a
// responder, lo que el browser ve como un "Network Error" crudo sin
// detalle. Caso real reportado 2026-10-27: PDF de 33 MB sin mensaje claro.
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const formatMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

// Botón de adjuntar con menú "Foto" (input accept="image/*", sin `capture`
// para que el navegador ofrezca elegir cámara o galería — pedido explícito
// del usuario, no cámara directa) vs "Archivo" (cualquier tipo permitido por
// el backend: PDF, Word, Excel, etc). Pensado para usarse ágilmente desde el
// celular durante la consulta, sin perder la opción de adjuntar documentos
// desde la compu.
const AttachFileButton = ({
  onFilesSelected, onFilesRejected, multiple = false, loading = false, disabled = false,
  label = 'Adjuntar', variant = 'outlined', size = 'small',
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const picked = multiple ? Array.from(e.target.files || []) : (e.target.files?.[0] ? [e.target.files[0]] : []);
    e.target.value = '';
    if (picked.length === 0) return;

    const tooBig = picked.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    const selected = picked.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);

    if (tooBig.length > 0) {
      const detail = tooBig.map((f) => `${f.name} (${formatMb(f.size)} MB)`).join(', ');
      const message = `El límite por archivo es 20 MB. Comprimilo o subí uno más liviano: ${detail}`;
      if (onFilesRejected) onFilesRejected(message, tooBig);
      else window.alert(message);
    }
    if (selected.length > 0) onFilesSelected(multiple ? selected : selected[0]);
  };

  return (
    <>
      <input ref={photoInputRef} type="file" accept="image/*" multiple={multiple} hidden onChange={handleChange} />
      <input ref={fileInputRef} type="file" multiple={multiple} hidden onChange={handleChange} />
      <Button
        size={size} variant={variant} disabled={disabled || loading}
        startIcon={loading ? <CircularProgress size={12} /> : <AttachFileIcon />}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{ fontSize: '0.72rem' }}
      >
        {label}
      </Button>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem dense onClick={() => { setMenuAnchor(null); photoInputRef.current?.click(); }}>
          <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Foto (cámara o galería)</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => { setMenuAnchor(null); fileInputRef.current?.click(); }}>
          <ListItemIcon><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Archivo (PDF, Word, etc.)</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default AttachFileButton;
